import { subWeeks, startOfWeek } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import ApiService from './api-service';

const fragments = `
  fragment NewContactEventParts on NewContactEvent {
    type
    date
    metadata {
      contact {
        _id
        ContactID
        Name
      }
      schedule {
        name
      }
    }
  }
  fragment ThresholdLimitReachedEventParts on ThresholdLimitReachedEvent {
    type
    date
    metadata {
      contact {
        _id
        ContactID
        Name
      }
      limit
    }
  }
  fragment NewContactNoteEventParts on NewContactNoteEvent {
    type
    date
    metadata {
      hash
      contact {
        _id
        ContactID
        Name
      }
      invoices {
        InvoiceID
        InvoiceNumber
      }
      user {
        _id
        profile {
          firstName
          lastName
        }
      }
    }
  }
  fragment ResumedEventParts on ResumedEvent {
    type
    date
    metadata {
      user {
        profile {
          firstName
          lastName
        }
      }
      scheduled
    }
  }
  fragment PausedEventParts on PausedEvent {
    type
    date
    metadata {
      until
      user {
        profile {
          firstName
          lastName
        }
      }
    }
  }
  fragment ReplyEventParts on ReplyEvent {
    type
    date
    metadata {
      messageId
      contact {
        Name
        FirstName
        LastName
        ContactID
      }
      invoices {
        InvoiceID
        InvoiceNumber
        emails {
          subject
          messageId
          from {
            address
          }
          to {
            address
          }
          cc {
            address
          }
          canReplyNowWithUserId
        }
      }
      from {
        address
        name
      }
      response
    }
  }
  fragment CustomerStatementReplyEventParts on CustomerStatementReplyEvent {
    type
    date
    metadata {
      messageId
      contact {
        Name
        FirstName
        LastName
        ContactID
      }
      invoices {
        InvoiceID
        InvoiceNumber
      }
      from {
        address
        name
      }
      response
    }
  }
  fragment RecentEventDisputeParts on ChaseFeedRecentEventDisputeType {
    type
    date
    metadata {
      invoice {
        _id
        InvoiceID
        InvoiceNumber
        contact {
          Name
          FirstName
          LastName
          ContactID
        }
      }
      createdBy {
        profile {
          firstName
          lastName
        }
      }
      hash
      inDispute
    }
  }
  fragment RecentEventMonitoringParts on CreditMonitoringEvent {
    type
    date
    metadata {
      count
      eventDate
    }
  }
  fragment PaymentEventParts on PaymentEvent {
    type
    date
    metadata {
      contact {
        _id
        ContactID
        Name
      }
      debitType
    }
  }
  fragment ScheduleChangeEventParts on ScheduleEvent {
    type
    date
    metadata {
      contacts {
        Name
        ContactID
      }
      user {
        profile {
          firstName
          lastName
        }
      }
      schedule {
        name
      }
    }
  }
  fragment RecentEventInvoiceGroupingMethodChangeParts on InvoiceGroupingMethodChangeEvent {
    type
    date
    metadata {
       user {
        _id
        profile {
          firstName
          lastName
        }
      }
      enabled
      days
    }
  }

  fragment RecentEventNewTaskParts on ChaseFeedRecentEventNewTaskType {
    type
    date
    metadata {
      type
      contact {
        Name
        FirstName
        LastName
        ContactID
      }
      invoices {
        InvoiceID
        InvoiceNumber
      }
      assignee {
        profile {
          firstName
          lastName
        }
      }
      result
    }
  }
`;

export const chaseFeedQuery = `
  query {
    organisation {
      BaseCurrency
      reports {
        currentOutstanding {
          total
          totalInvoices
          overdue
          totalOverdueInvoices
          updatedAt
          days {
            from
            to
            total
            totalInvoices
          }
        }
      }
      chaseFeed {
        dso(latestNumberOfMonths: 6) {
          year
          month
          daySalesOutstandingAvg
          numberOfInvoicesConsidered
          updatedAt
        }
        chasing {
          lastChase {
            date
            bounced {
              totalCount
              amount
              invoices {
                contact {
                  ContactID
                }
              }
            }
            unopened {
              totalCount
              amount
            }
            paid {
              amount
              totalCount
            }
            chased {
              totalCount
              amount
              invoices {
                _id
                InvoiceNumber
                CurrencyCode
                DueDate
                AmountDue
                contact {
                  ContactID
                  Name
                  _id
                  collectionDetails {
                    notes
                    options {
                      latePaymentFees
                    }
                  }
                }
              }
            }
          }
          upcomingChase {
            date
            readyToChase {
              amount
              totalCount
            }
            endOfSchedule {
              totalCount
            }
            cantBeChased {
              amount
              totalCount
            }
            missingEmail {
              totalCount
            }
            missingMobile {
              totalCount
            }
            missingAddress {
              totalCount
            }
            missingName {
              totalCount
            }
          }
        }
      }
    }
  }
`;

export const chaseFeedInsightsQuery = `
  query($startDate: Date, $endDate: Date) {
    organisation {
      chaseFeed {
        insights(startDate: $startDate, endDate: $endDate) {
          insights {
            totalAmountChased
            channels {
              channel
              totalSent
              totalInvoices
              invoiceIds
              totalAmount
            }
          }
          paid {
            today {
              currency
              amount
              totalCount
              invoiceIds
            }
            period {
              currency
              amount
              totalCount
              invoiceIds
            }
          }
          paidChased {
            period {
              invoiceIds {
                invoiceIds
              }
            }
          }
          bounced {
            period {
              amount
              totalCount
              invoiceIds
            }
          }
        }
        chasing(startDate: $startDate, endDate: $endDate) {
          lastChase {
            chased {
              amount
              totalCount
            }
          }
        }
      }
    }
  }
`;

export const chaseFeedQueryRecentEvents = `
  ${fragments}
  query {
    organisation {
      chaseFeed {
        recentEvents {
          totalCount
          nodes {
            ...NewContactNoteEventParts
            ...PausedEventParts
            ...ResumedEventParts
            ...ReplyEventParts
            ...ThresholdLimitReachedEventParts
            ...NewContactEventParts
            ...PaymentEventParts
            ...CustomerStatementReplyEventParts
            ...RecentEventDisputeParts
            ...RecentEventMonitoringParts
            ...ScheduleChangeEventParts
            ...RecentEventInvoiceGroupingMethodChangeParts
            ...RecentEventNewTaskParts
          }
        }
      }
    }
  }
`;

const ChasefeedService = {
  async get(ShortCode) {
    const body = {
      query: chaseFeedQuery,
    };
    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);

    return {
      chasing: data.organisation.chaseFeed.chasing,
      currentOutstanding: data.organisation.reports.currentOutstanding,
      dso: data.organisation.chaseFeed.dso?.reverse(),
    };
  },
  async getInsights({ ShortCode, startDate, endDate }) {
    const body = {
      query: chaseFeedInsightsQuery,
      variables: {
        startDate,
        endDate,
      },
    };
    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);

    return {
      ...data.organisation.chaseFeed?.insights,
      lastChase: data.organisation.chaseFeed?.chasing?.lastChase,
    };
  },
  async getRecentEventsByDefault(ShortCode) {
    const body = {
      query: chaseFeedQueryRecentEvents,
    };
    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);

    return {
      recentEvents: data.organisation.chaseFeed.recentEvents,
    };
  },
  async getRecentEvents({ after, type, ShortCode }) {
    const body = {
      query: `
        ${fragments}
        query($after: Date, $filter: ChaseFeedFilter) {
          organisation {
            chaseFeed {
              recentEvents(after: $after, filter: $filter) {
                totalCount
                nodes {
                  ...NewContactNoteEventParts
                  ...PausedEventParts
                  ...ResumedEventParts
                  ...ReplyEventParts
                  ...ThresholdLimitReachedEventParts
                  ...NewContactEventParts
                  ...PaymentEventParts
                  ...CustomerStatementReplyEventParts
                  ...RecentEventDisputeParts
                  ...RecentEventMonitoringParts
                  ...ScheduleChangeEventParts
                  ...RecentEventInvoiceGroupingMethodChangeParts
                  ...RecentEventNewTaskParts
                }
              }
            }
          }
        }
      `,
      variables: {
        after,
        filter: { type },
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);

    return data.organisation.chaseFeed.recentEvents;
  },
  async getDashboardMetrics(ShortCode) {
    const body = {
      query: `query {
        organisation {
          metricsDashboard {
            updatedAt 
            metricName
            value {
              total
              totalSeconds
              totalPaidCents
              paidPercentage
            }
          }
        }
      }`,
    };
    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);

    return data.organisation.metricsDashboard;
  },
  async getMetrics({ ShortCode, metricName, timezone }) {
    const getPeriodAnchorDates = () => {
      const now = new Date();
      const orgTimezone = timezone || 'UTC';

      return Array.from({ length: 5 }, (_, index) => {
        const zonedDate = utcToZonedTime(subWeeks(now, index), orgTimezone);
        const startOfIsoWeek = startOfWeek(zonedDate, { weekStartsOn: 1 });
        startOfIsoWeek.setHours(0, 0, 0, 0);

        return zonedTimeToUtc(startOfIsoWeek, orgTimezone);
      });
    };

    const body = {
      query: `query($metricNames: [String]!, $aggregationPeriod: String!, $periodAnchorDates: [Date]!) {
        organisation {
          metrics(metricNames: $metricNames, aggregationPeriod: $aggregationPeriod, periodAnchorDates: $periodAnchorDates) {
            updatedAt
            metricName
            periodAnchorDate
            value {
              total
              totalSeconds
              totalPaidCents
              paidPercentage
              byChannel {
                email
                sms
                phone_call
                letter
              }
            }
          }
        }
      }`,
      variables: {
        metricNames: [metricName],
        aggregationPeriod: 'weekly',
        periodAnchorDates: getPeriodAnchorDates(),
      },
    };
    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);

    return data.organisation.metrics;
  },
};

export default ChasefeedService;
